import { useCallback, useEffect, useState } from 'react';
import { AiVideoModelEnum, axiosInstance, modelDetails } from '../../utils';
import { AiModelDetail } from './useAiModelManagement';
import { AiVideoModelDetails } from 'apps/t2c-fe/@types';
import { useAppContext } from '../context';

export function useAiModelDetails() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { userData, isUserDataLoading } = useAppContext();

  const [models, setModels] = useState<Record<AiVideoModelEnum, AiVideoModelDetails>>(modelDetails);
  const [modelsData, setModelsData] = useState([]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const res = await axiosInstance.get('/api/users/ai-modelsdetail');
        const payload: AiModelDetail[] = res?.data || [];

        if (!mounted) return;

        // Clone base config
        const merged: Record<AiVideoModelEnum, AiVideoModelDetails> = {
          ...modelDetails,
        };

        setModelsData(payload);

        for (const beModel of payload) {
          const modelName = beModel.modelName as AiVideoModelEnum;
          const uiConfig = modelDetails[modelName];

          if (!uiConfig) continue;

          merged[modelName] = {
            ...uiConfig,
            ...(beModel.alphaGenerationLimit === 0 && beModel.generationType === 'video'
              ? { isPremium: true }
              : {}),
          };
        }

        setModels(merged);
      } catch (err) {
        console.error(err);
        setError('Failed to load AI models');
      } finally {
        setLoading(false);
      }
    }
    if (userData) load();
    return () => {
      mounted = false;
    };
  }, [userData]);

  const getModelData = useCallback(
    (modelName: string) => {
      // Guard: invalid or empty modelName
      if (!modelName || typeof modelName !== 'string') {
        return {
          modelName,
          isPremium: false,
        };
      }

      // Guard: modelsData not ready or not an array
      if (!Array.isArray(modelsData)) {
        return {
          modelName,
          isPremium: false,
        };
      }

      const modelConfig = modelsData.find((model) => model?.modelName === modelName);

      // If not found → return safe default
      if (!modelConfig) {
        return {
          modelName,
          isPremium: false,
        };
      }

      return {
        ...modelConfig,
        modelName: modelConfig.modelName ?? modelName,
        isPremium: Number(modelConfig.alphaGenerationLimit) === 0,
      };
    },
    [modelsData]
  );

  return { models, modelsData, getModelData, loading, error };
}
